import React, { useEffect, useState } from 'react';
import { Typography, Chip } from '@mui/material';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { useTexture } from 'src/hook/useTexture';
import { Granit } from 'src/services/granits';
import { getPiece } from 'src/utils/configurator.utils';
import {
  getIndexType,
  getLocalIndex,
  isTwoSteleEqual,
  getDimensionsWithGeometry,
  getGlobalIndex,
} from 'src/utils/3d.utils';
import {
  getAssemblyStockWithoutGranit,
  getStocksByPieceId,
  getStocksByStele,
} from 'src/services/stock';
import { TexturesProps, SortType } from './types';
import { Container, ChipContainer, GranitContainer } from './styles';
import {
  useActiveGranits,
  useFilteredGranits,
  useSortedGranits,
} from './hooks';
import {
  changeTexture,
  selectedTexture,
  selectPieceWithGranitId,
} from './utils';
import { TextureFilters } from './TextureFilters';
import { TextureSort } from './TextureSort';
import { TextureOrigin } from './TextureOrigin';
import { TextureSelection } from './TextureSelection';
import { GranitCard } from './GranitCard';
import { AdvancedMode } from 'src/components/3d/Fragments/AdvancedMode';
import { StateNames } from 'src/Screens/Configurator';

export const Textures = ({
  activePieces,
  setActivePieces,
  pieceIndex,
  customSteles,
  setCustomSteles,
  customPlatings,
  setCustomPlatings,
  setPieceIndex,
  getValue,
  pieces,
  setValue,
  customSoles,
  setCustomSoles,
  history,
}: TexturesProps) => {
  const { buckets } = useEnv();
  const { granits } = useTexture();
  const [selectedDominant, setSelectedDominant] = useState<string>();
  const [hasSelectedAllPieces, setHasSelectedAllPieces] =
    useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [error, setError] = useState<string>();
  const [sort, setSort] = useState<SortType>('ascending_name');
  const [categories, setCategories] = useState<string[]>([]);
  const [origin, setOrigin] = React.useState<string[]>([]);
  const [seeAssemblyStock, setSeeAssemblyStock] = useState<boolean>(false);

  const type = getIndexType(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
  );
  const piece = getPiece(
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    pieceIndex,
  );

  if (
    selectedDominant &&
    activePieces[pieceIndex]?.granit?.id !== selectedDominant
  ) {
    setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
  }

  const { data: stockData, onRefresh: refreshStock } = useLoadDataList(
    () =>
      getStocksByPieceId(
        piece.id,
        getDimensionsWithGeometry(piece?.geometry?.attributes?.position?.array),
      ),
    [piece.id, piece],
    type === 'activePieces',
  );

  const { data: stockStelesData, onRefresh: refreshStockSteles } =
    useLoadDataList(
      () =>
        getStocksByStele({
          baseIndex: Number(piece.baseIndex),
          boxDownWidth: Number(piece.boxDownWidth),
          boxHeight: Number(piece.boxHeight),
          boxLeftHeight: Number(piece.boxLeftHeight),
          boxRightHeight: Number(piece.boxRightHeight),
          boxUpWidth: Number(piece.boxUpWidth),
          headIndex: Number(piece.headIndex),
          depth: Number(piece.depth),
        }),
      [piece.id],
      type === 'customStele',
    );

  const { data: assemblyStockData, onRefresh: refreshAssemblyStock } =
    useLoadDataList(
      () =>
        getAssemblyStockWithoutGranit({
          stockPieces: pieces.map((el) => {
            const activePiece = activePieces.find((p) => p.id === el.id);

            const { x, y, z } = getDimensionsWithGeometry(
              el.geometry.attributes.position.array,
            );

            const weight = Math.round(
              (activePiece?.granit?.weight ?? 0) * ((x * y * z) / 100),
            );

            return {
              pieceId: el.id ?? -1,
              granitId: activePiece.granit?.id ?? '',
              newGeometry: [...el.geometry.attributes.position.array],
              x,
              y,
              z,
              weight,
            };
          }),
        }),
      [pieces],
      type === 'activePieces',
    );

  useEffect(() => {
    if (type === 'activePieces') {
      refreshStock();
    }
  }, [piece.id, type]);

  const activeGranits = useActiveGranits(
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
  );

  const filteredGranits = useFilteredGranits(
    granits,
    pieceIndex,
    activePieces,
    filter,
    origin,
    categories,
    seeAssemblyStock,
    assemblyStockData,
  );

  const sortedGranits = useSortedGranits(filteredGranits, sort);

  const handleChangeGranit = (granit: Granit) => {
    setError(undefined);
    if (selectedDominant) {
      const piecesIdsToChange: number[] = [];
      for (const [elementIndex, element] of activePieces.entries()) {
        if (element.granit?.id === selectedDominant) {
          if (element.underAssembly) {
            if (element.underAssembly.fabrications.includes(granit.fabrication))
              piecesIdsToChange.push(elementIndex);
          } else if (element.fabrications.includes(granit.fabrication)) {
            piecesIdsToChange.push(elementIndex);
          }
        }
      }
      for (const [elementIndex, element] of customSteles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customStele',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      for (const [elementIndex, element] of customPlatings.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customPlating',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      for (const [elementIndex, element] of customSoles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customSole',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      changeTexture(
        granit,
        piecesIdsToChange,
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setError,
      );
      setSelectedDominant(granit.id);
    } else if (hasSelectedAllPieces) {
      const piecesIdsToChange: number[] = [];

      for (const [elementIndex, element] of activePieces.entries()) {
        if (
          element.fabrications?.includes(granit.fabrication) ||
          (element.underAssembly?.fabrications?.includes(granit.fabrication) &&
            element.material === granit.material)
        ) {
          piecesIdsToChange.push(elementIndex);
        }
      }
      for (const [elementIndex] of customSteles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customStele',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );

        piecesIdsToChange.push(globalIndex);
      }
      for (const [elementIndex] of customPlatings.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customPlating',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        piecesIdsToChange.push(globalIndex);
      }
      for (const [elementIndex] of customSoles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customSole',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        piecesIdsToChange.push(globalIndex);
      }

      changeTexture(
        granit,
        piecesIdsToChange,
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setError,
      );
    } else {
      changeTexture(
        granit,
        [pieceIndex],
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setError,
      );
    }
  };

  const currentSelectedTexture = selectedTexture(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
  );

  const handleSelectPieceWithGranitId = (granitId: string) => {
    selectPieceWithGranitId(
      granitId,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      setPieceIndex,
    );
  };

  return (
    <Container>
      <AdvancedMode getValue={getValue} setValue={setValue} />
      <ChipContainer>
        <TextureSelection
          hasSelectedAllPieces={hasSelectedAllPieces}
          setHasSelectedAllPieces={setHasSelectedAllPieces}
          activeGranits={activeGranits}
          selectedDominant={selectedDominant}
          setSelectedDominant={setSelectedDominant}
          buckets={buckets ? buckets : { BUCKET_3D_PIECES: '' }}
          activePieces={activePieces}
          pieceIndex={pieceIndex}
          setValue={(stateName: string, value: any) =>
            setValue(stateName as StateNames, value)
          }
          selectPieceWithGranitId={handleSelectPieceWithGranitId}
        />
      </ChipContainer>

      {error ? (
        <Typography
          variant='subtitle2'
          sx={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            marginBottom: '12px',
            paddingBottom: '16px',
            paddingTop: '16px',
          }}
          color='error'
        >
          {error}
        </Typography>
      ) : (
        <Typography
          variant='body2'
          color='#667085'
          sx={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '12px',
            paddingBottom: '16px',
            paddingTop: '16px',
          }}
        >
          Ou sélectionner une pièce sur le monument pour changer le granit.
        </Typography>
      )}

      <TextureFilters
        filter={filter}
        setFilter={setFilter}
        categories={categories}
        setCategories={setCategories}
      />

      <Chip
        style={{ marginTop: '10px', padding: '8px' }}
        label={`Voir le stock de monument ${
          activeGranits.length > 1
            ? activeGranits
                .filter((el) => el.id !== currentSelectedTexture?.id)
                .map((el) => el.granit?.name)
                .join('/ ')
            : ''
        }${activeGranits.length > 1 ? '/...' : ''}`}
        onClick={() => {
          if (seeAssemblyStock) {
            setSelectedDominant(undefined);
          } else {
            setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
          }

          setSeeAssemblyStock(!seeAssemblyStock);
        }}
        color={seeAssemblyStock ? 'primary' : 'default'}
      />

      <TextureSort sort={sort} setSort={setSort} />
      <TextureOrigin origin={origin} setOrigin={setOrigin} />

      <GranitContainer>
        {sortedGranits.map((el, index) => {
          let hasStock = false;

          if (type === 'customStele') {
            hasStock = !!stockStelesData?.find((stock) => {
              return (
                isTwoSteleEqual(stock.stele, piece) && stock.granitId === el.id
              );
            });
          } else {
            hasStock = !!stockData?.find((stock) => stock.granitId === el.id);
          }

          if (seeAssemblyStock) hasStock = true;

          return (
            <GranitCard
              key={index}
              granit={el}
              imageUrl={`${buckets?.BUCKET_3D_PIECES || ''}/${el.image}`}
              isSelected={currentSelectedTexture?.name === el.name}
              hasStock={hasStock}
              onClick={() => handleChangeGranit(el)}
            />
          );
        })}

        {!sortedGranits || sortedGranits.length === 0 ? (
          <Typography variant='body2' color='#667085'>
            Aucun granit disponible
          </Typography>
        ) : null}
      </GranitContainer>
    </Container>
  );
};
